import {type UIChild, UI} from "../../../stem-core/ui/UIBase";
import type {NodeAttributes} from "../../../stem-core/ui/NodeAttributes";
import type {BaseFlowHandler} from "../flows/BaseFlowHandler";
import type {AuthFlowStep} from "../flows/steps/AuthFlowStep";

import {iFrameState} from "../../services/IFrameState";
import {registerStyle} from "../../../stem-core/ui/style/Theme";
import {merchantStyleRule} from "../Security";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../../../blink-sdk/utils/AnalyticsClient";
import {PanelLink, redirectOnClickLink} from "./PanelLink.jsx";
import {sanitizeUrlFromOptions} from "../../../stem-core/ui/primitives/Link.jsx";
import {GATE_TYPE} from "../../../blink-sdk/Constants.js";
import {IFrameMessages} from "../../../blink-sdk/messaging/IFrameMessages";
import {iFrameConnection} from "../../services/IFrameConnection";
import {Flow} from "../flows/Flow.js";
import {startFlow} from "../flows/FlowEntrypoint.js";
import {BlinkButton, BlinkButtonStyle} from "../../../core/ui/Button.jsx";


class PanelActionButtonStyle extends BlinkButtonStyle {
    @merchantStyleRule
    panelActionButtonStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        marginBottom: 0,
        width: "100%",
        maxWidth: 300,
        ...iFrameState.actionButtonStyle,
    };
}

// TODO @cleanup WTF do we need a button to be an "a"???
@registerStyle(PanelActionButtonStyle)
export class PanelActionButton extends UI.Primitive("a", BlinkButton) {
    extraNodeAttributes(attr: NodeAttributes) {
        super.extraNodeAttributes(attr);
        attr.addClass(this.styleSheet.panelActionButtonStyle);
    }

    onMount() {
        super.onMount();
        // TODO @branch Hack to make AAJ paywall button
        setTimeout(() => {this.redraw();});
        this.addClickListener(() => dispatchAnalyticsEvent(AnalyticsEventType.ELEMENT_CLICK));
    }
}

// The auth step stands in for a handler here as it does in Flow.updateActiveFlow; only panelType is read
export function handleActionButtonClicked(flowController: BaseFlowHandler | AuthFlowStep, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    event.returnValue = false;

    const {redirectUrl} = iFrameState.iframeParams;
    if (redirectUrl) {
        iFrameConnection.sendToSDK(IFrameMessages.REDIRECT_TO_URL, {url: redirectUrl});
        return;
    }

    const {gateType} = iFrameState;
    const {inlineFlow} = iFrameState.iframeParams;
    const {panelType} = flowController;

    if (gateType === GATE_TYPE.popup || inlineFlow) {
        // Just continue the flow here, we're already in a popup
        iFrameState.iframeParams.skipCTA = true;
        Flow.updateActiveFlow(panelType);
    } else {
        // Start a flow independently from this panel
        startFlow(panelType, {
            skipCTA: true,
        })
    }
}

export function MakeActionButton(flowController: BaseFlowHandler | AuthFlowStep, defaultOptions?: Record<string, any>) {
    return (options: Record<string, any>, children?: UIChild) => {
        options = {
            label: iFrameState.iframeParams.buttonText,
            journeyAlias: iFrameState.iframeParams.journeyAlias,
            testId: "panelActionButton",
            value: options.label,
            ...defaultOptions,
            ...options,
        };

        if (flowController) {
            options.onClick = (...args: [any]) => handleActionButtonClicked(flowController, ...args);
        }

        // TODO @cleanup why is PanelActionButton an anchor? Why does it take in a href????
        if (options.href != null) {
            sanitizeUrlFromOptions(options, "href");
            options.onClick = redirectOnClickLink;
            if (options.newTab) {
                options.target = "_blank";
            }
        }
        if (options.link) {
            return <PanelLink {...options}>
                {children}
            </PanelLink>;
        }
        return <PanelActionButton {...options}>
            {children}
        </PanelActionButton>;
    };
}

